import { Link } from "react-router";

import { ExplainedBadge } from "@/shared/components/ExplainedBadge";
import { STATUS_BADGES } from "@/shared/components/StatusBadge";
import { FormattedDateTime } from "@/shared/datetime";
import {
  type SessionIngestionCounts,
  type SessionRecord,
  waitingFileCount,
} from "@/shared/domain/sessions";

import { inactivityPhrase } from "./inactivityPhrase";
import { CompletionDescription } from "./SessionCompletion";
import { type SessionBadge } from "./sessionIngestionBadge";
import { sessionIngestionPanelLink } from "./sessionIngestionPanelLink";

export interface SessionBadgesProps {
  session: SessionRecord;
  /** Shown as complete. Its ingestion status can say so before its record does. */
  complete: boolean;
  /** The ingestion badge beside the status, if any. */
  ingestion: SessionBadge | null;
  /** The session's file counts, once loaded. */
  counts: SessionIngestionCounts | undefined;
  /** Whether hovering opens the popups. Turn it off in lists. */
  openOnHover?: boolean;
}

/**
 * The session's status badge and, beside it, its ingestion badge. Clicking
 * either, or hovering it unless `openOnHover` is off, says what it means. The
 * session page and the sessions list both show these.
 */
export function SessionBadges({
  session,
  complete,
  ingestion,
  counts,
  openOnHover = true,
}: SessionBadgesProps) {
  return (
    <>
      {complete ? (
        <CompleteBadge
          session={session}
          counts={ingestion === null ? counts : undefined}
          openOnHover={openOnHover}
        />
      ) : (
        <InProgressBadge session={session} openOnHover={openOnHover} />
      )}
      {ingestion !== null && (
        <IngestionBadge
          badge={ingestion}
          session={session}
          complete={complete}
          counts={counts}
          openOnHover={openOnHover}
        />
      )}
    </>
  );
}

function InProgressBadge({
  session,
  openOnHover,
}: {
  session: SessionRecord;
  openOnHover: boolean;
}) {
  const status = STATUS_BADGES.session.in_progress;
  return (
    <ExplainedBadge
      status={status}
      aria-label={`${status.label}: what does this mean?`}
      openOnHover={openOnHover}
      explanation={
        <>
          <p>Files can still be added.</p>
          <p data-testid="session-status-completes">
            <WhenItCompletes session={session} />
          </p>
        </>
      }
      data-testid="session-status-badge"
    />
  );
}

/** How an in-progress session will become complete. */
function WhenItCompletes({ session }: { session: SessionRecord }) {
  const policy = session.completion_policy;
  if (policy === null) {
    return <>Stays in progress until someone marks it complete.</>;
  }
  if (session.completes_at === null) {
    return (
      <>
        Completes automatically {inactivityPhrase(policy.inactivity_minutes)}{" "}
        after the last file is added.
      </>
    );
  }
  return (
    <>
      Completes at <FormattedDateTime value={session.completes_at} timeZone />{" "}
      unless another file is added.
    </>
  );
}

function CompleteBadge({
  session,
  counts,
  openOnHover,
}: {
  session: SessionRecord;
  /** The file counts, when no ingestion badge shows them. */
  counts: SessionIngestionCounts | undefined;
  openOnHover: boolean;
}) {
  const status = STATUS_BADGES.session.complete;
  return (
    <ExplainedBadge
      status={status}
      aria-label={`${status.label}: what does this mean?`}
      openOnHover={openOnHover}
      explanation={
        <>
          {/* A div: the principal link renders one, which a <p> can't hold. */}
          <div>
            {/* A record read before the session completed can't say when. */}
            {session.status === "complete" ? (
              <CompletionDescription session={session} />
            ) : (
              "Completed"
            )}
            .
          </div>
          <p>Adding a file puts it back in progress.</p>
        </>
      }
      actions={counts !== undefined && <Counts counts={counts} />}
      data-testid="session-status-badge"
    />
  );
}

function IngestionBadge({
  badge,
  session,
  complete,
  counts,
  openOnHover,
}: {
  badge: SessionBadge;
  session: SessionRecord;
  complete: boolean;
  counts: SessionIngestionCounts | undefined;
  openOnHover: boolean;
}) {
  const loaded = counts !== undefined && badge.placeholder === undefined;
  return (
    <ExplainedBadge
      status={badge.status}
      label={badge.label}
      aria-label={`${badge.label}: what does this mean?`}
      openOnHover={openOnHover}
      explanation={
        loaded ? (
          <IngestionExplanation
            session={session}
            complete={complete}
            counts={counts}
          />
        ) : (
          <UnloadedExplanation failed={badge.placeholder === "unknown"} />
        )
      }
      actions={loaded && <Counts counts={counts} />}
      data-testid="session-ingestion-badge"
    />
  );
}

/** What "Checking…" or "Status unknown" means. */
function UnloadedExplanation({ failed }: { failed: boolean }) {
  return failed ? (
    <>
      <p>The ingestion status couldn&rsquo;t be loaded.</p>
      <p>Reload the page to try again.</p>
    </>
  ) : (
    <>
      <p>Roboto is checking whether its files are ingested.</p>
      <p>Check back in a moment.</p>
    </>
  );
}

function IngestionExplanation({
  session,
  complete,
  counts,
}: {
  session: SessionRecord;
  complete: boolean;
  counts: SessionIngestionCounts;
}) {
  const panel = sessionIngestionPanelLink(session.session_id);
  const pending = counts.pending_file_count;
  const failed = counts.failed_file_count;
  if (pending > 0 && failed > 0) {
    const them = failed === 1 ? "it" : "them";
    return (
      <>
        <p>
          Ingestion failed on{" "}
          <Link to={panel}>
            {failed} of {fileCount(pending)}
          </Link>{" "}
          Roboto is waiting for.
        </p>
        <p>
          Run {them} again, or choose not to wait for {them}, from the{" "}
          <Link to={panel}>list of files</Link>.
        </p>
      </>
    );
  }
  if (pending > 0) {
    return (
      <>
        <p>
          <Link to={panel}>{fileCount(pending)}</Link>{" "}
          {pending === 1 ? "is" : "are"} waiting to be ingested.
        </p>
        <p>
          {complete
            ? "The session is fully ingested once these are done."
            : "The session is fully ingested once it’s complete and these are done."}
        </p>
      </>
    );
  }
  const skipped = counts.skipped_file_count;
  return (
    <>
      <p>Every file Roboto waited for is ingested.</p>
      <p>
        {skipped > 0 ? (
          <>
            Someone chose not to wait for{" "}
            <Link to={panel}>{fileCount(skipped)}</Link>.
          </>
        ) : (
          "You can visualize and query their topics."
        )}
      </p>
    </>
  );
}

/** The popup's footer: "Files: 2 ingested · 3 waiting · 1 failed". */
function Counts({ counts }: { counts: SessionIngestionCounts }) {
  const line = ingestionCountsLine(counts);
  return line === null ? null : (
    <span data-testid="session-status-counts">Files: {line}</span>
  );
}

/** "3 files", "1 file". */
function fileCount(count: number): string {
  return `${count} ${count === 1 ? "file" : "files"}`;
}

/**
 * The session's files by where their ingestion stands: "2 ingested · 3
 * waiting · 1 failed · 1 stored as-is · 2 not waited for". Null when it has
 * none.
 */
function ingestionCountsLine(counts: SessionIngestionCounts): string | null {
  const parts = [
    [counts.ingested_file_count, "ingested"],
    [waitingFileCount(counts), "waiting"],
    [counts.failed_file_count, "failed"],
    [counts.not_ingestable_file_count, "stored as-is"],
    [counts.skipped_file_count, "not waited for"],
  ] as const;
  const nonzero = parts.filter(([count]) => count > 0);
  if (nonzero.length === 0) {
    return null;
  }
  return nonzero.map(([count, what]) => `${count} ${what}`).join(" · ");
}
